import React, { FC, useMemo } from 'react';
import { Popover } from 'antd';
import { isWalthroughTaskAnswered } from '../../utils/SiteSurveyUtils';

const YELLOW = '#FFB648';
const GREEN = '#135200';
const GREY_OUTLINE = '#D1D5DB';
const WHITE = '#FFFFFF';

const DEFAULT_SIZE = 20;
const STROKE_WIDTH = 1;

const describeSlice = (cx, cy, radius, ratio) => {
	if (ratio <= 0 || ratio >= 1) return '';
	const angle = ratio * 2 * Math.PI;
	const endX = cx + radius * Math.sin(angle);
	const endY = cy - radius * Math.cos(angle);
	const largeArc = ratio > 0.5 ? 1 : 0;
	return `M ${cx} ${cy} L ${cx} ${
		cy - radius
	} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY} Z`;
};

interface TaskGroupProgressCircleProps {
	completed: number;
	total: number;
	size?: number;
}

export const TaskGroupProgressCircle: FC<TaskGroupProgressCircleProps> = ({
	completed,
	total,
	size = DEFAULT_SIZE,
}) => {
	const ratio = total > 0 ? Math.min(1, Math.max(0, completed / total)) : 0;
	const cx = size / 2;
	const cy = size / 2;
	const radius = size / 2 - STROKE_WIDTH / 2;
	const slicePath = useMemo(() => describeSlice(cx, cy, radius, ratio), [cx, cy, radius, ratio]);
	const isComplete = ratio >= 1;

	return (
		<svg
			width={size}
			height={size}
			style={{ display: 'block', flexShrink: 0 }}
			aria-label={`${completed} of ${total} tasks answered`}
			role="img"
		>
			{isComplete ? (
				<>
					<circle
						cx={cx}
						cy={cy}
						r={radius}
						fill={GREEN}
						stroke={GREY_OUTLINE}
						strokeWidth={STROKE_WIDTH}
					/>
					<polyline
						points={`${size * 0.28},${size * 0.52} ${size * 0.45},${size * 0.68} ${size * 0.74},${
							size * 0.36
						}`}
						fill="none"
						stroke={WHITE}
						strokeWidth={2}
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</>
			) : (
				<>
					<circle
						cx={cx}
						cy={cy}
						r={radius}
						fill={WHITE}
						stroke={GREY_OUTLINE}
						strokeWidth={STROKE_WIDTH}
					/>
					{ratio > 0 && <path d={slicePath} fill={YELLOW} />}
				</>
			)}
		</svg>
	);
};

interface GroupedTaskProgressCircleProps {
	groupedAnswers: any[];
	size?: number;
}

export const GroupedTaskProgressCircle: FC<GroupedTaskProgressCircleProps> = ({
	groupedAnswers,
	size,
}) => {
	const { completed, total } = useMemo(() => {
		const answers = groupedAnswers || [];
		return {
			completed: answers.filter(isWalthroughTaskAnswered).length,
			total: answers.length,
		};
	}, [groupedAnswers]);

	if (total === 0) return null;
	return <TaskGroupProgressCircle completed={completed} total={total} size={size} />;
};

interface TaskGroupProgressCircleWithPopoverProps {
	groupedAnswers: any[];
	size?: number;
}

export const TaskGroupProgressCircleWithPopover: FC<TaskGroupProgressCircleWithPopoverProps> = ({
	groupedAnswers,
	size,
}) => {
	const { completed, total } = useMemo(() => {
		const answers = groupedAnswers || [];
		return {
			completed: answers.filter(isWalthroughTaskAnswered).length,
			total: answers.length,
		};
	}, [groupedAnswers]);

	if (total === 0) return null;
	const completedColor = completed === 0 ? 'inherit' : completed >= total ? GREEN : YELLOW;
	const content = (
		<span>
			Answered: <span style={{ color: completedColor }}>{`${completed}/${total}`}</span>
		</span>
	);
	return (
		<Popover content={content}>
			<span style={{ display: 'inline-flex' }}>
				<TaskGroupProgressCircle completed={completed} total={total} size={size} />
			</span>
		</Popover>
	);
};

interface GroupedTaskUnansweredDisplayProps {
	groupedAnswers: any[];
	onClick?: () => void;
	active?: boolean;
}

export const GroupedTaskUnansweredDisplay: FC<GroupedTaskUnansweredDisplayProps> = ({
	groupedAnswers,
	onClick,
	active,
}) => {
	const { completed, total } = useMemo(() => {
		const answers = groupedAnswers || [];
		return {
			completed: answers.filter(isWalthroughTaskAnswered).length,
			total: answers.length,
		};
	}, [groupedAnswers]);

	if (completed === 0 || completed >= total) return null;

	return (
		<div
			className={`flex flex-row items-center gap-2 hover:bg-[#FFFBE6] ${
				active ? 'bg-[#FFFBE6]' : 'bg-transparent'
			}`}
			onClick={onClick}
			onMouseDown={(event) => event.preventDefault()}
			tabIndex={-1}
			style={{
				padding: '2px 12px',
				borderRadius: 16,
				outline: 'none',
				WebkitTapHighlightColor: 'transparent',
				cursor: onClick ? 'pointer' : undefined,
			}}
		>
			<div className="h5regular text-[#4B5563]">Unanswered:</div>
			<div className="h5bold underline" style={{ color: 'rgb(171, 133, 15)' }}>
				{total - completed}
			</div>
		</div>
	);
};
